import { Logger } from '@utils'
import jsConvertCase from 'js-convert-case'

import { MenuTreeNodeType, MenuType } from '@redux/types'
import { MenuTreeNode, I18nType } from '@schemas'

interface GetPathByParentIdParamType {
	menuTreeList: MenuTreeNodeType[]
	parentId: string
	name: string
}

export const Path = {
	getUnduplicatedMenuDirName: (menuNameArr: string[], paramName: string): string => {
		try {
			const { targetName, duplicatedName } = Path.getDuplicatedName({
				menuNameArr,
				paramName,
				isDir: true,
			})

			if (duplicatedName.length === 0) {
				return paramName
			} else {
				const similarNameArr: string[] = Path.getSimilarName(menuNameArr, targetName)
				const maxSuffix: number = Path.getNameSuffix(similarNameArr, targetName)
				const newName = `${targetName} ${maxSuffix + 1}`

				return newName
			}
		} catch (error) {
			Logger.warn(error)
			return ''
		}
	},

	getUnduplicatedMenuObjectName: (menuNameArr: string[], paramName: string): string => {
		try {
			const { targetName, duplicatedName } = Path.getDuplicatedName({
				menuNameArr,
				paramName,
				isDir: false,
			})

			if (duplicatedName.length === 0) {
				return paramName
			} else {
				const similarNameArr: string[] = Path.getSimilarName(menuNameArr, targetName)
				const maxSuffix: number = Path.getNameSuffix(similarNameArr, targetName)
				const newName = `${targetName} ${maxSuffix + 1}`

				return newName
			}
		} catch (error) {
			Logger.warn(error)
			return ''
		}
	},

	getDuplicatedName: ({
		menuNameArr,
		paramName,
		isDir,
	}: {
		menuNameArr: string[]
		paramName: string
		isDir: boolean
	}): { targetName: string; duplicatedName: string[] } => {
		const trimedParamNamen: string = paramName.trim()
		const newFolderNumericSuffix = parseInt(trimedParamNamen.split(' ')[2])
		const defaultName: string = isDir ? 'New Folder' : 'New Object'

		const isNewFolderName: boolean =
			trimedParamNamen.match(defaultName) !== null && !isNaN(newFolderNumericSuffix)

		const targetName: string = isNewFolderName ? defaultName : trimedParamNamen
		const duplicatedName: string[] = menuNameArr.filter(aName => {
			const isSameName: boolean = aName === paramName

			return isSameName
		})

		const result = {
			targetName: targetName,
			duplicatedName: duplicatedName,
		}

		return result
	},

	getSimilarName: (menuNameArr: string[], targetName: string): string[] => {
		const result: string[] = menuNameArr.filter((aName: string) => {
			const isStartWithTargetId: boolean = aName.startsWith(targetName)

			return isStartWithTargetId
		})

		return result
	},

	getNameSuffix: (menuNameArr: string[], targetName: string): number => {
		const suffixArr: number[] = []
		for (const aName of menuNameArr) {
			const similarNameParts: string[] = aName.split(targetName)
			const suffix: string = similarNameParts[similarNameParts.length - 1].trim()
			const numericSuffix: number = parseInt(suffix)
			if (!isNaN(numericSuffix)) {
				suffixArr.push(numericSuffix)
			}
		}

		const result: number = suffixArr.length > 0 ? Math.max(...suffixArr) : 1

		return result
	},

	getPathByName: (path: string, name: string): string => {
		try {
			const pathParts: string[] = path.split('/')
			pathParts.splice(-1)
			pathParts.push(name)

			const result: string = pathParts.join('/')

			return result
		} catch (error) {
			Logger.warn(error)

			return ''
		}
	},

	getUriByPath: (path: string): string => {
		try {
			const pathParts: string[] = path.split('/')
			const result = pathParts
				.map(aPath => jsConvertCase.toKebabCase(aPath))
				.join('/')
				.replace('root/', '/')

			return result
		} catch (error) {
			Logger.warn(error)

			return ''
		}
	},

	getPathByParentIdAndName: ({
		menuTreeList,
		parentId,
		name,
	}: GetPathByParentIdParamType): string => {
		try {
			const parentIdArr: string[] = []
			const pathNameArr: string[] = []
			let n = 0

			// insert first descendantId
			pathNameArr.push(name)
			parentIdArr.push(parentId)

			while (!parentIdArr.includes('root') && n < 5) {
				const newDescendantId: string = parentIdArr[parentIdArr.length - 1]
				const newParent: { parent: string; name: string } = Path.getParentId(
					menuTreeList,
					newDescendantId,
				)

				parentIdArr.push(newParent.parent)
				pathNameArr.push(newParent.name)
				n++
			}

			pathNameArr.push('root')
			pathNameArr.reverse()

			const pathArr: string[] = [...pathNameArr]
			const result: string = pathArr.join('/')

			return result
		} catch (error) {
			Logger.warn(error)

			return ''
		}
	},

	getParentId: (
		menuTreeList: MenuTreeNodeType[],
		descendantId: string,
	): { parent: string; name: string } => {
		const ownNode: MenuTreeNodeType[] = menuTreeList.filter(
			(aNode: MenuTreeNodeType) => aNode.id === descendantId,
		)

		const tempParentId: string | number = ownNode[0].parent
		const tempName: string | number = ownNode[0].text

		const parentId: string = tempParentId.toString() ?? tempParentId
		const name: string = tempName

		return { parent: parentId, name: name }
	},

	convertTreeNodeToMenu: (menuTreeList: MenuTreeNodeType[]): MenuType[] => {
		const sortedTreeList: MenuTreeNodeType[] = menuTreeList.filter(
			aNode => aNode.isActivate ?? false,
		)

		sortedTreeList.sort((a: MenuTreeNodeType, b: MenuTreeNodeType) => {
			const aPathParts = a.path?.split('/') ?? ['root']
			const bPathParts = b.path?.split('/') ?? ['root']
			const aOrder: number = a.order ?? 0
			const bOrder: number = b.order ?? 0

			let order = 0
			if (a.parent === 'root' && b.parent === 'root') {
				if (a.droppable && b.droppable) {
					if (aOrder === bOrder) {
						if (a.text < b.text) {
							order = -1
						} else {
							order = 1
						}
					} else if (aOrder < bOrder) {
						order = -1
					} else {
						order = 1
					}
				} else {
					if (a.droppable) {
						order = -1
					} else {
						order = 1
					}
				}
			} else if (a.parent === 'root') {
				order = -1
			} else if (b.parent === 'root') {
				order = 1
			} else {
				if (aPathParts.length < bPathParts.length) {
					order = -1
				} else if (aPathParts.length > bPathParts.length) {
					order = 0
				}
			}

			return order
		})

		const result: MenuType[] = Path.treeify({ data: sortedTreeList })

		return result
	},

	treeify: ({
		data = [],
		targetId = 'root',
	}: {
		data: MenuTreeNodeType[]
		targetId?: string
	}): MenuType[] => {
		const result: MenuType[] = data.reduce((prev: any[], next) => {
			if (next.parent === targetId) {
				const id: string = next.id.toString() ?? next.id
				const newMenu: MenuType = {
					// ...next,
					id: id,
					name: next.text,
					icon: next.icon,
					i18n: next.i18n,
				}

				if (!next.droppable) {
					newMenu.to = next.uri
				}

				const children = Path.treeify({
					data: data,
					targetId: id,
				})

				if (children.length > 0) {
					newMenu.children = children
				}

				prev.push(newMenu)
			}

			return prev
		}, [])

		return result
	},

	orderMenuTree: (treeNodeList: MenuTreeNodeType[]): MenuTreeNodeType[] => {
		const res = [...treeNodeList]

		res.sort((a: MenuTreeNodeType, b: MenuTreeNodeType) => {
			const aPathParts = a.path?.split('/') ?? ['root']
			const bPathParts = b.path?.split('/') ?? ['root']
			const aOrder: number = a.order ?? -1
			const bOrder: number = b.order ?? -1

			const isSameDroppable: boolean =
				(a.droppable === true && b.droppable === true) ||
				(a.droppable === false && b.droppable === false)
			const isSameOrder: boolean = a.order === b.order
			const isSamePathDepth: boolean = aPathParts.length === bPathParts.length
			let order = 0

			if (isSameDroppable) {
				if (isSamePathDepth) {
					if (isSameOrder) {
						if (a.text < b.text) {
							order = -1
						} else if (a.text > b.text) {
							order = 1
						}
					} else if (aOrder < bOrder) {
						order = -1
					} else if (aOrder > bOrder) {
						order = 1
					}
				} else if (aPathParts.length < bPathParts.length) {
					order = -1
				} else if (aPathParts.length > bPathParts.length) {
					order = 1
				}
			} else if (a.droppable !== true && b.droppable === true) {
				order = 1
			} else if (a.droppable === true && b.droppable !== true) {
				order = -1
			}

			return order
		})

		return res
	},

	reorderDroppedMenuTree: (treeNodeList: MenuTreeNodeType[]): MenuTreeNodeType[] => {
		const tempTree = treeNodeList.map((aNode, idx) => {
			const parentId: string = aNode.parent.toString() ?? aNode.parent
			const newPath: string = Path.getPathByParentIdAndName({
				menuTreeList: treeNodeList,
				parentId: parentId,
				name: aNode.text,
			})
			const newUri: string = Path.getUriByPath(newPath)

			return {
				...aNode,
				order: idx,
				parent: parentId,
				path: newPath,
				uri: newUri,
			}
		})

		tempTree.sort((a: MenuTreeNodeType, b: MenuTreeNodeType) => {
			const aPathParts = a.path?.split('/') ?? ['root']
			const bPathParts = b.path?.split('/') ?? ['root']
			const isSamePathDepth: boolean = aPathParts.length === bPathParts.length
			const aOrderNumber = a.order ?? 0
			const bOrderNumber = b.order ?? 0
			const isSameOrder: boolean = aOrderNumber === bOrderNumber

			const order: number =
				isSamePathDepth && isSameOrder && !a.droppable
					? 1
					: isSamePathDepth && aOrderNumber < bOrderNumber
					? -1
					: isSamePathDepth && aOrderNumber > bOrderNumber
					? 1
					: aPathParts.length < bPathParts.length
					? -1
					: aPathParts.length > bPathParts.length
					? 1
					: 0

			return order
		})

		const res = tempTree.map((aNode2, idx) => {
			const id: string = aNode2.id.toString() ?? aNode2.id
			const sameParentOrder = treeNodeList
				.filter(aTempNode => aTempNode.parent === aNode2.parent)
				.findIndex(aTempNode2 => aTempNode2.id === aNode2.id)
			const order: number = aNode2.parent === 'root' ? idx : sameParentOrder
			const i18n: I18nType[] = (aNode2.i18n as any as I18nType[]) ?? []

			const newNode = new MenuTreeNode({
				...aNode2,
				id: id,
				order: order,
				text: aNode2.text,
				i18n: i18n as any,
			})

			return newNode
		})

		return res
	},

	checkDuplicatedNameInSamePath: (treeNodeList: MenuTreeNodeType[]): boolean => {
		const valueArr = treeNodeList.map(aNode => {
			return `${aNode.parent}/${aNode.text}[${aNode.droppable ? 'folder' : 'object'}]`
		})
		const isDuplicated: boolean = valueArr.some((item, idx) => {
			return valueArr.indexOf(item) != idx
		})

		return isDuplicated
	},

	findDescendants: (treeNodeList: MenuTreeNodeType[], parentIds: string[]) => {
		const descendantNodeList = treeNodeList.filter(aNode => {
			const parentId: string = aNode.parent.toString() ?? aNode.parent
			return parentIds.includes(parentId)
		})
		const descendantParentIds: string[] = descendantNodeList.map(
			aNode2 => aNode2.id.toString() ?? aNode2.id,
		)

		const descendantNodeList2 =
			descendantParentIds.length > 0
				? Path.findDescendants(treeNodeList, descendantParentIds)
				: []

		const result: MenuTreeNodeType[] = [...descendantNodeList, ...descendantNodeList2]

		return result
	},
}
